<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  closeLabel: {
    type: String,
    default: '',
  },
  desktopClass: {
    type: String,
    default: '',
  },
  panelClass: {
    type: String,
    default: '',
  },
  contentClass: {
    type: String,
    default: '',
  },
  contentWrapperClass: {
    type: String,
    default: 'min-h-0 flex-1 overflow-y-auto p-4 md:overflow-visible md:p-0',
  },
  showMobileTitle: {
    type: Boolean,
    default: false,
  },
  ignoreClickOutside: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const isMobile = ref(false);
const sectionRef = ref(null);

onClickOutside(sectionRef, () => emit('close'), {
  ignore: props.ignoreClickOutside,
});

onMounted(() => {
  isMobile.value = window.innerWidth < 768;
  if (isMobile.value) document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="contents">
    <Teleport to="body" :disabled="!isMobile">
      <div
        class="fixed inset-0 z-[60] flex items-end justify-center bg-n-alpha-black1 backdrop-blur-[4px] transition-colors duration-200 ease-in-out md:contents md:bg-transparent md:backdrop-blur-none"
        @click.self="emit('close')"
      >
        <Transition
          appear
          enter-active-class="transition-transform duration-200 ease-in-out md:transition-none"
          leave-active-class="transition-transform duration-200 ease-in-out md:transition-none"
          enter-from-class="translate-y-full md:translate-y-0"
          enter-to-class="translate-y-0"
          leave-from-class="translate-y-0"
          leave-to-class="translate-y-full md:translate-y-0"
        >
          <section
            ref="sectionRef"
            class="flex h-[92vh] max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] bg-n-solid-2 shadow-xl md:z-40 md:h-auto md:max-h-none md:w-auto md:overflow-visible md:rounded-xl md:border md:border-n-weak md:bg-n-alpha-3 md:shadow-lg md:backdrop-blur-[100px]"
            :class="[desktopClass, panelClass]"
            @click.stop
          >
            <header
              class="flex items-center justify-start px-4 pb-2 pt-4 md:hidden"
            >
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                size="sm"
                type="button"
                class="!rounded-full bg-n-alpha-black2"
                :aria-label="closeLabel"
                @click="emit('close')"
              />
              <h3
                v-if="showMobileTitle"
                class="ms-3 min-w-0 truncate text-base font-medium text-n-slate-12"
              >
                {{ title }}
              </h3>
            </header>
            <div :class="contentWrapperClass">
              <div :class="contentClass">
                <slot />
              </div>
            </div>
          </section>
        </Transition>
      </div>
    </Teleport>
  </div>
</template>
